import { Link } from 'react-router-dom';
import { ArrowLeft, Download, ExternalLink, FileText } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { Button } from './ui/button';
import { openExternalUrl, PUBLIC_WEB_URL } from '../lib/externalLinks';
import Header from './Header';
import MobileNav from './MobileNav';

/**
 * Shared layout for Privacy / Terms — PDF is source of truth.
 * Place files at frontend/public/privacy-policy.pdf and terms-of-service.pdf
 */
export default function LegalDocumentPage({
  title,
  pdfPath,
  intro,
  icon: Icon = FileText,
}) {
  const pdfHref = `${process.env.PUBLIC_URL || ''}${pdfPath}`;
  const isNative = Capacitor.isNativePlatform();

  return (
    <div className="min-h-screen bg-background pb-safe">
      <Header />
      <div className="max-w-3xl mx-auto px-4 pt-20 pb-24">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <div className="bg-card border rounded-2xl p-6 sm:p-8 space-y-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Icon className="w-5 h-5 text-primary" />
              <h1 className="text-2xl font-bold">{title}</h1>
            </div>
            <p className="text-sm text-muted-foreground">SkipdQ · Last updated: 2026</p>
          </div>

          {intro ? (
            <p className="text-sm leading-6 text-foreground">{intro}</p>
          ) : null}

          <div className="flex flex-wrap gap-3">
            {isNative ? (
              <Button
                className="rounded-full"
                onClick={() => openExternalUrl(`${PUBLIC_WEB_URL}${pdfPath}`)}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Open PDF
              </Button>
            ) : (
              <>
                <Button asChild className="rounded-full">
                  <a href={pdfHref} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open PDF
                  </a>
                </Button>
                <Button asChild variant="outline" className="rounded-full">
                  <a href={pdfHref} download>
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </a>
                </Button>
              </>
            )}
          </div>

          <p className="text-xs text-muted-foreground">
            If the PDF does not open, confirm{' '}
            <code className="text-[11px] bg-muted px-1 rounded">{pdfPath}</code> is deployed
            under the site root.
          </p>

          {/* Desktop embed; mobile browsers often prefer native PDF viewer via Open */}
          <div className="hidden md:block border rounded-xl overflow-hidden bg-muted/30">
            <iframe
              title={title}
              src={pdfHref}
              className="w-full h-[70vh] border-0"
            />
          </div>
        </div>
      </div>
      <MobileNav />
    </div>
  );
}
